import { Button, KakaoMap } from "@/components";
import { useDesignMaxTheme } from "@/packages";
import { useSetWidgetTouchState } from "@/shared/client";
import { useGetLocation, type MapInfoResponse } from "@/shared/server";
import { FONT } from "@/styles";
import { Box, Center, chakra, Flex, Spinner, Text } from "@chakra-ui/react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";

export const Location = () => {
    const { data } = useGetLocation<MapInfoResponse>({ section: "location" });
    const { ref, inView } = useInView({ threshold: 0.8, delay: 0.3, triggerOnce: true });
    const setMapTouchState = useSetWidgetTouchState();
    const { MEDIAS } = useDesignMaxTheme();
    const { isMobile, isLaptop, isDesktop } = MEDIAS();
    const handleWheel = (event: React.WheelEvent) => {
        event.stopPropagation();
    };

    const handleTouchStart = () => {
        setMapTouchState(true);
    };
    const handleTouchEnd = () => {
        setMapTouchState(false);
    };

    const contentPCHeight = isLaptop ? "528PX" : "792px";
    const contentPCPaddingRight = isLaptop ? "88px" : "256px";
    const motionContentPCPadding = isLaptop ? "48px" : "80px 80px 80px 24px";

    const addressBoxPCMaxWidth = isLaptop ? "467.5px" : "528px";
    const mapBoxPCMaxWidth = isLaptop ? "467.5px" : "848px";

    return data ? (
        <Box
            position="relative"
            width="100%"
            h="100dvh"
            ref={ref}
            background={`url(${data.imgUrl})no-repeat center/cover`}
        >
            <Box
                position="absolute"
                w="100%"
                maxH={isMobile ? "552PX" : "none"}
                h={isMobile ? "100%" : contentPCHeight}
                top="50%"
                left="50%"
                zIndex="1"
                transform="translate(-50%, -50%)"
                justifyContent="flex-end"
                paddingRight={isMobile ? "0" : contentPCPaddingRight}
            >
                <MotionLocationContainer
                    position="relative"
                    display="flex"
                    flexDir="column"
                    rowGap={isLaptop ? "16px" : "24px"}
                    w="100%"
                    height="100%"
                    padding={isMobile ? "48px 24px" : motionContentPCPadding}
                    alignItems="flex-end"
                    borderRightRadius="16px"
                    background="whiteAlpha.900"
                    initial={{ x: "-90%" }}
                    animate={inView ? { x: ["-90%", 0], transition: { duration: 0.6 } } : {}}
                >
                    <Flex
                        maxW={isDesktop ? "1424px" : "1744px"}
                        w="100%"
                        maxH={isLaptop ? "405px" : "632px"}
                        h="100%"
                        flexDir="column"
                        rowGap={isMobile ? "16px" : "8px"}
                    >
                        <Flex w="100%" flexDir="column" rowGap={isMobile ? "24px" : "8px"}>
                            <Text {...FONT("noto", isLaptop ? 16 : 20, 500)}>펜션오시는 길</Text>
                            <Text
                                sx={
                                    isMobile
                                        ? { ...FONT("cormorant", 20, 500, 1.2) }
                                        : { ...FONT("cormorant", isLaptop ? 32 : 48, 500, 1.2) }
                                }
                            >
                                Location
                            </Text>
                        </Flex>
                        <Flex
                            flex="1"
                            width="100%"
                            flexDir={isMobile ? "column" : "row"}
                            columnGap={isLaptop ? "16px" : "48px"}
                            rowGap={isMobile ? "16px" : "0"}
                        >
                            <Flex
                                maxWidth={isMobile ? "none" : addressBoxPCMaxWidth}
                                w="100%"
                                flexDir="column"
                                justifyContent="space-between"
                                height={isMobile ? "114px !important" : "auto"}
                            >
                                <Box>
                                    <Text
                                        sx={
                                            isMobile
                                                ? { ...FONT("notoSerif", 14, 500) }
                                                : { ...FONT("notoSerif", 16, 500) }
                                        }
                                    >
                                        도로명 : {data.pensionInfo.addressDetail.street}
                                    </Text>
                                    <Text
                                        sx={
                                            isMobile
                                                ? { ...FONT("notoSerif", 14, 500) }
                                                : { ...FONT("notoSerif", 16, 500) }
                                        }
                                    >
                                        지번 : {data.pensionInfo.addressDetail.jibun}
                                    </Text>
                                </Box>
                                <Flex columnGap="16px">
                                    <Button>
                                        <Text {...FONT("pretendard", 16, 500)}>네이버 길찾기</Text>
                                    </Button>
                                    <Button>
                                        <Text {...FONT("pretendard", 16, 500)}>카카오 길찾기</Text>
                                    </Button>
                                </Flex>
                            </Flex>
                            <Box
                                maxWidth={isMobile ? "none" : mapBoxPCMaxWidth}
                                w="100%"
                                h="100%"
                                onWheel={handleWheel}
                                onTouchStart={handleTouchStart}
                                onTouchEnd={handleTouchEnd}
                            >
                                <KakaoMap latLng={data.latLng} name={data.pensionInfo.name.kr} />
                            </Box>
                        </Flex>
                    </Flex>
                </MotionLocationContainer>
            </Box>
        </Box>
    ) : (
        <Center h="100vh" columnGap="8px">
            <Text>LOADING</Text>
            <Spinner />
        </Center>
    );
};
const MotionLocationContainer = chakra(motion.div);
